import { supabase } from './supabase'
import { User } from '@supabase/supabase-js'

export interface SignUpData {
  email: string
  password: string
  fullName: string
  phone?: string
}

export interface SignInData {
  email: string
  password: string
}

export interface UserProfile {
  id: string
  email: string
  full_name: string
  phone?: string
  avatar_url?: string
  created_at: string
  updated_at: string
}

/**
 * Kullanıcı kaydı
 */
export async function signUp({ email, password, fullName, phone }: SignUpData) {
  // Admin e-posta adresleriyle kayıt olunmasını engelle
  if (email.includes('@petunyaborek.com')) {
    throw new Error('Bu e-posta adresi ile kayıt olamazsınız. Lütfen farklı bir e-posta adresi kullanın.')
  }

  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      emailRedirectTo: undefined, // Supabase'in kendi email'ini engelle
      data: {
        full_name: fullName,
        phone: phone || null,
      }
    }
  })

  if (error) {
    throw error
  }

  // Başarılı kayıttan sonra hoş geldin maili gönder
  if (data.user && !error) {
    try {
      // Hoş geldin maili gönder
      const { generateWelcomeEmail, sendEmail } = await import('./email')
      const welcomeEmail = generateWelcomeEmail(email, fullName)
      await sendEmail(welcomeEmail)
      console.log('✅ Hoş geldin maili gönderildi:', email)
    } catch (emailError) {
      console.error('❌ Hoş geldin maili gönderme hatası:', emailError)
      // Email hatası kayıt işlemini engellemez
    }
  }

  return data
}

/**
 * Kullanıcı girişi
 */
export async function signIn({ email, password }: SignInData) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  })

  if (error) {
    throw error
  }

  return data
}

/**
 * Kullanıcı çıkışı
 */
export async function signOut() {
  const { error } = await supabase.auth.signOut()
  
  if (error) {
    throw error
  }
}

/**
 * Mevcut kullanıcıyı getir
 */
export async function getCurrentUser(): Promise<User | null> {
  const { data: { user } } = await supabase.auth.getUser()
  return user
}

/**
 * Kullanıcı profilini getir
 */
export async function getUserProfile(userId: string): Promise<UserProfile | null> {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('id', userId)
    .single()

  if (error) {
    console.error('Profil getirilirken hata:', error)
    return null
  }

  return data as unknown as UserProfile
}

/**
 * Kullanıcı profilini güncelle
 */
export async function updateUserProfile(userId: string, updates: Partial<UserProfile>) {
  const { data, error } = await supabase
    .from('user_profiles')
    .update({
      ...updates,
      updated_at: new Date().toISOString()
    })
    .eq('id', userId)
    .select()
    .single()

  if (error) {
    throw error
  }

  return data
}

/**
 * Şifre sıfırlama e-postası gönder
 */
export async function resetPassword(email: string) {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  })

  if (error) {
    throw error
  }
}

/**
 * Şifre güncelle
 */
export async function updatePassword(newPassword: string) {
  const { error } = await supabase.auth.updateUser({
    password: newPassword
  })

  if (error) {
    throw error
  }
}

/**
 * E-posta doğrulama durumunu kontrol et
 */
export function isEmailConfirmed(user: User | null): boolean {
  return user?.email_confirmed_at !== null
}

/**
 * Auth state değişikliklerini dinle
 */
export function onAuthStateChange(callback: (user: User | null) => void) {
  return supabase.auth.onAuthStateChange((_event, session) => {
    callback(session?.user ?? null)
  })
}



/**
 * Email confirmation token'ını doğrula
 */
export async function verifyEmailConfirmation(token: string): Promise<{
  success: boolean
  user_id?: string
  email?: string  
  message: string
}> {
  try {
    const { data, error } = await supabase.rpc('verify_confirmation_token', {
      p_token: token,
      p_token_type: 'email_confirmation'
    })

    if (error) {
      return {
        success: false,
        message: 'Token doğrulaması sırasında hata oluştu: ' + error.message
      }
    }

    const result = Array.isArray(data) ? data[0] : null
    return {
      success: result?.success || false,
      user_id: result?.user_id,
      email: result?.email,
      message: result?.message || 'Bilinmeyen hata'
    }
  } catch (error) {
    return {
      success: false,
      message: 'Token doğrulaması başarısız: ' + (error as Error).message
    }
  }
}